import * as React from 'react';
import './index.css';
import { withTranslation } from '@sailor/i18n-mach-pro';
import { connect } from '@wmfe/mach-pro-store';
import { RootState } from '@/src/pages/homepage/home_page_main/models/store';
import { getAttachments } from '@/src/components/HomePage/ContentItems';
import { ContentSpecType } from '@/src/components/HomePage/ContentItems/itemSuperCard/itemSuperCard';
import { View } from '@wmfe/mach-pro-components-react';
import { reportBizCardMetrics, findTargetFromMatrix } from '@/src/utils/bizCardReporter';
import { ModuleItemProps } from '../common/interface';
import { reportProductCardMc, reportProductCardMv, setCid } from './lxReport';
import PicturesSwiper from './swiper';
import processLink from './processLink';
import { DynamicTagGroup } from '../../DynamicTag';
import ImgOverlay from '../ImgOverlay';
import { ShopStatus } from '../FeedsShopCardWithDishes/constant';
import VoteSpecialWrapper from './VoteSpecialWrapper';

const WMRouter = Mach.requireModule('Router');

const mapStateToProps = (state: RootState) => ({
  feedAPIData: state.global?.feedAPIData,
  pageNo: state.global?.pageNo,
});

type BaseIProps = ReturnType<typeof mapStateToProps>;

interface IProps extends BaseIProps, ModuleItemProps {
  puzzleContainerRef?: any;
}

interface IState {}

class ProductBigPicCard extends React.Component<IProps, IState> {
  biz_trace_id = '';

  constructor(props) {
    super(props);
    setCid(this.props.apiData?.log_info?.cid);
    this.biz_trace_id = props.pageLogData?.biz_trace_id || '-999';
  }

  componentWillUnmount() {}

  attrChecker = (): void => {
    const data = this.props?.apiData?.json_data || {};
    const { shopMainPicUrl, content, scheme } = data || {};

    const shopMainPicMissing = shopMainPicUrl ? 0 : 1;
    const shopNameMissing = findTargetFromMatrix(content, 'shopName')?.name ? 0 : 1;
    const shopJumpUrlMissing = scheme ? 0 : 1;

    reportBizCardMetrics({
      params: {
        shopMainPicMissing,
        shopNameMissing,
        shopJumpUrlMissing,
      },
      tags: {
        channel: '大图商卡',
      },
    });
  };

  lxReportOnClick = () => {
    reportProductCardMc(this.props.apiData?.json_data, this.props.apiData?.indexPath.item, this.biz_trace_id);
  };

  lxReportOnShow = (apiData) => {
    const { json_data: data } = apiData || {};
    if (data?.isMV) {
      return;
    }

    this.attrChecker();
    // 由于外部传入的时 CloneDeep 的数据，在列表数据更新时会导致 isMv 标志丢失, 造成 MV 上报重复，所以需要在全局做 MV 上报记录
    const mvKey = `${apiData?.indexPath?.item}_key`;
    if (globalThis.homeProductCardMVHistory === undefined) {
      globalThis.homeProductCardMVHistory = {};
    }
    const bizTraceId = this.biz_trace_id;
    // 如果第一个卡片要被重新曝光了，证明筛选条件已经发生变化或发生刷新，需要清空全局历史上报记录
    if (apiData?.indexPath?.item === 0 && this.props.pageNo === 0 && bizTraceId !== globalThis.lastBizTraceId) {
      // 第一个数据会进行多次渲染，防止多次清空
      if (!this.props.feedAPIData?.isClearHomeProductCardMVHistory) {
        console.log('FeedProductBigPicCard lxReportOnShow clear history');
        globalThis.homeProductCardMVHistory = {};
        this.props.feedAPIData.isClearHomeProductCardMVHistory = true;
      }
    }
    if (globalThis.homeProductCardMVHistory[mvKey]) {
      return;
    }
    globalThis.lastBizTraceId = bizTraceId;
    reportProductCardMv(this.props.apiData?.json_data, this.props.apiData?.indexPath.item, bizTraceId);
    data.isMV = true;
    globalThis.homeProductCardMVHistory[mvKey] = true;
  };

  private gotoDetail(scheme?: string, shopInfoResponse?: any) {
    if (scheme) {
      WMRouter.navigateTo(scheme, { shopInfoResponse }, () => {});
    }
    this.lxReportOnClick();
  }

  private clickGroupInfo(originalUrl) {
    reportProductCardMc(this.props.apiData?.json_data, this.props.apiData?.indexPath.item, this.biz_trace_id, false);
    if (originalUrl) {
      const targetUrl = processLink(originalUrl, 'data', {
        lxInfo: {
          cid: this.props.apiData?.log_info?.cid,
          biz_trace_id: this.biz_trace_id,
          shop_brand_id: this.props.apiData?.json_data?.logData?.shop_brand_id,
        },
      });
      WMRouter.navigateTo(targetUrl, null, () => {});
    }
  }

  calcPictureWidth(marginStyle, isPictureFullCard = false) {
    const basePicWidth = Mach.env.screenWidth - 2 * 8;
    // 大图模式定制样式
    if (isPictureFullCard) {
      return basePicWidth - 8;
    }
    // 非大图模式逻辑与之前保持一致
    return basePicWidth - marginStyle.marginLeft - marginStyle.marginRight;
  }

  formImgBottomLabelData(imgBottomLabel, isPictureFullCard, marginStyle) {
    if (!imgBottomLabel) {
      return {};
    }
    const parsedData = { ...imgBottomLabel.labelDynamicTag };
    // 图底标签的宽度需要端上自己算下
    let tagWidth = Mach.env.screenWidth - 8 * 2 - 12 * 2;
    // 非全卡图模式还要减去卡片边距
    if (!isPictureFullCard) {
      tagWidth = tagWidth - marginStyle.marginLeft - marginStyle.marginRight;
    }
    parsedData.tags[0].width = tagWidth;
    return parsedData;
  }

  render() {
    // data check
    const { json_data, layout_info } = this.props.apiData || {};
    const {
      margin_top, margin_bottom, margin_left, margin_right
    } = layout_info || {};
    const {
      pictures,
      pictureRatio = { x: 16, y: 9 },
      shopStatus,
      content,
      scheme,
      shopMainPicUrl,
      imgTopContent: imgTopContentV1,
      imgTopContentV2,
      logo,
      brandLogo,
      shopGroupInfo,
      imgBottomLabel,
      pictureFullCard,
      imgOverlay
    } = json_data || {};
    // 样式兼容，优先使用v2的图片顶部标签
    const imgTopContent = imgTopContentV2
      ? { ...imgTopContentV2, type: 'v2' }
      : imgTopContentV1
        ? { ...imgTopContentV1, type: 'v1' }
        : null;
    const brandshoplogo = logo || brandLogo;
    // 卡片margin
    const marginStyle = {
      marginTop: margin_top || 0,
      marginBottom: margin_bottom || 12,
      marginLeft: margin_left || 12,
      marginRight: margin_right || 12,
    };
    // 图片
    let handlePictures = [...pictures];
    // main兜底
    if (handlePictures.length === 0) {
      handlePictures = [{ url: shopMainPicUrl, scheme }];
    }
    // 是否为大图模式
    const isPictureFullCard = pictureFullCard === 1;
    const pictureWidth = this.calcPictureWidth(marginStyle, isPictureFullCard);
    const pictureHeight = (pictureWidth * pictureRatio.y) / pictureRatio.x;
    let shopInfoResponse = json_data?.shopInfoResponse;
    shopInfoResponse = { ...shopInfoResponse, checkList: !!shopInfoResponse };

    const handleContent = content?.filter((subArr) => subArr?.length > 0);
    const showRestBg = shopStatus?.shopStatusCode === ShopStatus.CLOSED
      || shopStatus?.shopStatusCode === ShopStatus.OFFLINE
      || shopStatus?.shopStatusCode === ShopStatus.OUT_OF_RANGE;
    return (
      <View
        className="container"
        style={{ ...marginStyle, overflow: Mach.env.isAndroid ? 'visible' : 'hidden' }}
        onClick={() => {
          this.gotoDetail(scheme, shopInfoResponse);
        }}
        onView={(status: boolean) => {
          if (status) {
            this.lxReportOnShow(this.props.apiData);
          }
        }}
      >
        {/* 头图 */}
        {handlePictures && handlePictures?.length && (
          <view
            style={{
              width: pictureWidth,
              height: pictureHeight,
              alignSelf: 'center',
              marginTop: isPictureFullCard ? -10.5 : 0,
            }}
          >
            <PicturesSwiper
              pictures={handlePictures}
              shopStatus={shopStatus}
              imgTopContent={imgTopContent}
              brandLogo={brandshoplogo}
              // 有 imgOverlay 优先走 imgOverlay 的渲染逻辑
              disableMask={imgOverlay}
              style={{ width: pictureWidth, height: pictureHeight }}
              clickToDetail={(jumpUrl?: string) => {
                this.gotoDetail(jumpUrl, shopInfoResponse);
              }}
            />
            {/* 图片区域顶部标签 */}
            {imgTopContent && shopStatus && shopStatus?.shopStatusCode !== 4 && (
              <image
                style={{ width: imgTopContent?.width, height: imgTopContent?.height }}
                className={imgTopContent.type === 'v2' ? 'product-pic-top-brandPic-v2' : 'product-pic-top-brandPic'}
                src={imgTopContent?.picUrl}
                resizeMode="heightFix"
              />
            )}
            {/* 新动态标签腰封 */}
            {imgBottomLabel && (
              <view className="img-bottom-label-wrapper">
                <DynamicTagGroup {...this.formImgBottomLabelData(imgBottomLabel, isPictureFullCard, marginStyle)} />
              </view>
            )}

            {imgOverlay ? (
              <ImgOverlay imgOverlay={imgOverlay} shopStatus={shopStatus} isSmallCard customBorderRadius={12} />
            ) : (
              showRestBg && <view className="restBg" />
            )}
          </view>
        )}

        {/* [name [] / 闭店提醒[] / 决策信息[] / 动态标签[]] */}
        {handleContent.length > 0 && (
          <view className="procuct-content-container">
            {handleContent.map((warp, line) => {
              // 业务定制化逻辑： 投票控件会超出行高，判断下当前有投票控件时，超出部分展示，否则默认不展示
              const hasVoteArea = warp.some((item) => item.type === 'voteArea');
              const attachmentRender = (list) => list?.map((item, index) => {
                const attachmentData = getAttachments(item);
                const nextItemType = warp?.[Number(index) + 1]?.type;
                const nextAttachmentData = getAttachments(warp?.[Number(index) + 1]);

                const specStyle = nextItemType === 'shopName'
                  ? { specType: ContentSpecType.BlankSpace, spec: 8 }
                  : { specType: ContentSpecType.Point, spec: 4 };
                const attachmentStyle = item.type === 'shopScore' ? { customProperty: { hasBorder: true } } : {};

                if (attachmentData) {
                  return (
                    <attachmentData.attachment
                      key={`item.type_${line}_${index}`}
                      itemKey={item?.type}
                      data={item?.data}
                      specStyle={specStyle}
                      style={attachmentStyle}
                      hasLastItem={nextAttachmentData !== undefined && nextItemType !== 'voteArea'}
                      parentLogData={this.props.apiData?.json_data?.logData}
                    />
                  );
                }
                return null;
              });

              // 投票控件特化处理
              if (hasVoteArea) {
                return <VoteSpecialWrapper dataList={warp} marginStyle={marginStyle} line={line} attachmentRender={attachmentRender} parentLogData={this.props.apiData?.json_data?.logData} puzzleContainerRef={this.props.puzzleContainerRef} indexPath={this.props.apiData?.indexPath} />;
              }

              return warp?.length > 0 ? (
                <view key={line} className="procuct-content-item-container" style={{ marginTop: line === 0 ? 0 : 8 }}>
                  {attachmentRender(warp)}
                </view>
              ) : null;
            })}
          </view>
        )}

        {shopGroupInfo && shopGroupInfo.shopIdList?.length > 0 && (
          <view
            className="procuct-group-info"
            hitSlop={{
              top: 10,
              bottom: 10,
              left: 10,
              right: 10,
            }}
            onClick={() => {
              this.clickGroupInfo(shopGroupInfo.scheme);
            }}
          >
            <view className="procuct-group-info-line" />
            <view className="procuct-group-info-text-warp">
              <text className="procuct-group-info-text" content={shopGroupInfo?.text} />
              <image className="procuct-group-info-icon" src="assets://channel/shop-card-right-row-icon.png" autoRTL />
            </view>
          </view>
        )}
      </View>
    );
  }
}

export default connect(mapStateToProps)(
  withTranslation('', {
    withRef: true,
  })(ProductBigPicCard)
);
